/**
 * المرحلة 1.3 (P20) — «قيّم الزيارة»: one review per finished visit.
 *
 * POST /api/doctors/:id/reviews refuses a visit not yet completed and a
 * second review of the same one. A rating alone is published at once; a
 * comment waits for the institution's review first when its doctorReviews
 * policy says so (the default), and the patient is told which happened.
 */

import { useEffect, useState } from 'react';
import { Button, Field, Modal, Textarea } from '../../components/common';
import { doctorApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { Stars } from '../discovery/parts';

export function ReviewDialog({ booking, onClose, onSaved }) {
  const { t } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRating(0);
    setComment('');
  }, [booking]);

  if (!booking) return null;

  const save = async () => {
    setSaving(true);
    try {
      const result = await doctorApi.review(booking.doctorId, { bookingId: booking.id, rating, comment: comment.trim() || undefined });
      success(result?.review?.status === 'pending' ? t('review.savedPending') : t('review.saved'));
      onSaved?.();
    } catch (err) {
      const code = err.body?.code;
      toastError(code === 'REVIEW_EXISTS' ? t('review.exists') : code === 'VISIT_NOT_COMPLETED' ? t('review.notCompleted') : err.message);
      if (code === 'REVIEW_EXISTS') onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(booking)}
      onClose={onClose}
      title={t('review.title', { name: booking.doctorName || '' })}
      icon="star"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button icon="send" onClick={save} disabled={!rating} loading={saving} data-testid="review-send">
            {t('review.send')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="review-dialog">
        <div className="flex flex-col items-center gap-2">
          <Stars value={rating} onChange={setRating} size={30} />
          <p className="text-xs text-slate-400">{rating ? t(`review.word.${rating}`) : t('review.pick')}</p>
        </div>
        <Field label={t('review.comment')} hint={t('review.note')}>
          <Textarea rows={4} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('review.placeholder')} />
        </Field>
      </div>
    </Modal>
  );
}

export default ReviewDialog;
